import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  MessageCircle, Brain, BookMarked, Target, BookOpen,
  Mic, Trophy, Crown, Heart, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';

const QUICK_ACTION_ITEMS = [
  { to: '/free-chat', testId: 'free-chat-link', icon: MessageCircle, label: 'Free Chat', sub: 'Practice anytime', gradient: 'from-pink-500/10 to-pink-500/5', border: 'border-pink-500/20', ring: 'ring-2 ring-pink-500/30', iconBg: 'bg-gradient-to-br from-pink-500 to-purple-500', badge: 'bg-pink-500' },
  { to: '/flashcards', icon: Brain, label: 'Flashcards', sub: '500+ cards', gradient: 'from-purple-500/10 to-purple-500/5', border: 'border-purple-500/20', iconBg: 'bg-purple-500' },
  { to: '/stories', icon: BookMarked, label: 'Stories', sub: '40+ interactive', gradient: 'from-amber-500/10 to-amber-500/5', border: 'border-amber-500/20', iconBg: 'bg-amber-500' },
  { to: '/medical-german', testId: 'medical-german-link', icon: Target, label: 'Fachdeutsch', sub: 'Medical German', gradient: 'from-emerald-500/10 to-emerald-500/5', border: 'border-emerald-500/20', iconBg: 'bg-emerald-500' },
  { to: '/pflege-training', testId: 'pflege-training-link', icon: BookMarked, label: 'Pflege Training', sub: 'A1.1 Course', gradient: 'from-cyan-500/10 to-cyan-500/5', border: 'border-cyan-500/20', iconBg: 'bg-cyan-500' },
  { to: '/menschen-a1', testId: 'menschen-a1-link', icon: BookOpen, label: 'Menschen A1.1', sub: '12 Lektionen', gradient: 'from-blue-500/10 to-blue-500/5', border: 'border-blue-500/20', iconBg: 'bg-blue-500', badge: 'bg-blue-500' },
  { to: '/menschen-a12', testId: 'menschen-a12-link', icon: BookOpen, label: 'Menschen A1.2', sub: '12 Lektionen', gradient: 'from-indigo-500/10 to-indigo-500/5', border: 'border-indigo-500/20', iconBg: 'bg-indigo-500', badge: 'bg-indigo-500' },
  { to: '/menschen-a21', testId: 'menschen-a21-link', icon: BookOpen, label: 'Menschen A2.1', sub: '12 Lektionen', gradient: 'from-sky-500/10 to-sky-500/5', border: 'border-sky-500/20', iconBg: 'bg-sky-500', badge: 'bg-sky-500' },
  { to: '/menschen-a22', testId: 'menschen-a22-link', icon: BookOpen, label: 'Menschen A2.2', sub: '12 Lektionen', gradient: 'from-violet-500/10 to-violet-500/5', border: 'border-violet-500/20', iconBg: 'bg-violet-500', badge: 'bg-violet-500' },
  { to: '/bibliothek', testId: 'bibliothek-link', icon: BookOpen, label: 'Bibliothek', sub: '8 Bücher A1–A2', gradient: 'from-amber-500/10 to-amber-500/5', border: 'border-amber-500/20', iconBg: 'bg-amber-500', badge: 'bg-amber-500' },
  { to: '/voice-diary', testId: 'voice-diary-link', icon: Mic, label: 'Sprach-Tagebuch', sub: 'Voice Diary', gradient: 'from-emerald-500/10 to-teal-500/5', border: 'border-emerald-500/20', iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500', badge: 'bg-emerald-500' },
  { to: '/exam-simulation', testId: 'exam-simulation-link', icon: Trophy, label: 'Exam Simulation', sub: 'Goethe & telc', gradient: 'from-rose-500/10 to-rose-500/5', border: 'border-rose-500/20', iconBg: 'bg-gradient-to-br from-rose-500 to-orange-500', badge: 'bg-rose-500' },
  { to: '/exam-trainer', testId: 'exam-trainer-link', icon: Brain, label: 'Prüfungstrainer', sub: 'AI Exam Trainer', gradient: 'from-purple-500/10 to-purple-500/5', border: 'border-purple-500/20', iconBg: 'bg-gradient-to-br from-purple-500 to-indigo-500', badge: 'bg-purple-500' },
  { to: '/cultural-learning', testId: 'cultural-learning-link', icon: BookOpen, label: 'Cultural Learning', sub: 'German Life', gradient: 'from-indigo-500/10 to-indigo-500/5', border: 'border-indigo-500/20', iconBg: 'bg-indigo-500', badge: 'bg-indigo-500' },
];

export const QuickActionsGrid = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
    {QUICK_ACTION_ITEMS.map((item) => {
      const Icon = item.icon;
      return (
        <Link key={item.to} to={item.to} data-testid={item.testId}>
          <Card className={`cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 bg-gradient-to-br ${item.gradient} ${item.border} ${item.ring || ''}`}>
            <CardContent className="p-4 text-center">
              <div className={`w-12 h-12 rounded-full ${item.iconBg} flex items-center justify-center mx-auto mb-3`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <p className="font-semibold">{item.label}</p>
              <p className="text-xs text-muted-foreground">{item.sub}</p>
              {item.badge && <Badge className={`mt-2 ${item.badge} text-[10px]`}>NEW</Badge>}
            </CardContent>
          </Card>
        </Link>
      );
    })}
  </div>
);

export const SecondaryActionsGrid = ({ dailyStats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
    <Link to="/leaderboard">
      <Card className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 bg-gradient-to-br from-teal-500/10 to-teal-500/5 border-teal-500/20">
        <CardContent className="p-4 text-center">
          <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center mx-auto mb-3">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <p className="font-semibold">Leaderboard</p>
          <p className="text-xs text-muted-foreground">Weekly rankings</p>
        </CardContent>
      </Card>
    </Link>

    <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20">
      <CardContent className="p-4 text-center">
        <div className="flex items-center justify-center gap-1 mb-3">
          {[...Array(5)].map((_, i) => (
            <Heart key={`heart-${i}`} className={`h-5 w-5 ${i < (dailyStats?.hearts || 5) ? 'text-red-500 fill-red-500' : 'text-slate-300'}`} />
          ))}
        </div>
        <p className="font-semibold">Hearts</p>
        <p className="text-xs text-muted-foreground">{dailyStats?.hearts || 5}/5 remaining</p>
      </CardContent>
    </Card>

    <Link to="/offline-settings" data-testid="offline-settings-link">
      <Card className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
        <CardContent className="p-4 text-center">
          <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center mx-auto mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>
            </svg>
          </div>
          <p className="font-semibold">Offline Mode</p>
          <p className="text-xs text-muted-foreground">Download content</p>
        </CardContent>
      </Card>
    </Link>
  </div>
);
